import React from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import { getProfileSectionIcon } from './profileSectionIcons';

const statusColor = {
  generated: '#1976d2',
  reviewed: '#2e7d32',
  empty: '#64748b',
  too_long: '#c2410c',
};

const statusLabel = {
  generated: 'Generated',
  reviewed: 'Reviewed',
  empty: 'Empty',
  too_long: 'Too long',
};

const ProfileSectionCardGrid = ({ definitions = [], sections = [], interactive = false, onOpen }) => {
  const additional = definitions.find((item) => item.role === 'additional_knowledge');
  const standard = definitions.filter((item) => item.role !== 'additional_knowledge');
  const spacers = additional ? (5 - (standard.length % 3)) % 3 : 0;
  const cells = [
    ...standard,
    ...Array.from({ length: spacers }, (_, index) => ({ _id: `spacer-${index}`, spacer: true })),
    ...(additional ? [additional] : []),
  ];

  return (
    <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={1.25}>
      {cells.map((definition) => {
        if (definition.spacer) {
          return <Box key={definition._id} sx={{ display: { xs: 'none', md: 'block' } }} />;
        }
        const section = sections.find((item) => String(item.definitionId) === String(definition._id));
        const status = section?.status || 'empty';
        const Icon = getProfileSectionIcon(definition.icon);
        const isAdditional = definition.role === 'additional_knowledge';
        const preview = section?.entries?.[0];
        return (
          <Box
            key={definition._id}
            component={interactive ? 'button' : 'div'}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? () => onOpen(definition) : undefined}
            border={1}
            borderColor={isAdditional ? '#bfdbfe' : 'divider'}
            borderRadius={1.5}
            p={1.5}
            minHeight={148}
            display='flex'
            flexDirection='column'
            textAlign='left'
            bgcolor={isAdditional ? '#f8fbff' : '#fff'}
            sx={{ cursor: interactive ? 'pointer' : 'default', font: 'inherit' }}
          >
            <Box display='flex' alignItems='center' justifyContent='space-between' gap={1}>
              <Icon sx={{ fontSize: 28, color: 'text.secondary' }} />
              {isAdditional && (
                <Tooltip title='This card contains useful information that does not fit into the other sections. You can review and edit it like any other card.'>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
              )}
            </Box>
            <Typography variant='body2' fontWeight={700} sx={{ lineHeight: 1.3, mt: 1 }}>
              {definition.name}
            </Typography>
            {preview && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {preview.topic ? `${preview.topic}. ` : ''}
                {preview.text}
              </Typography>
            )}
            <Box mt='auto' pt={1}>
              <Typography variant='caption' fontWeight={700} sx={{ color: statusColor[status] }}>
                {statusLabel[status]}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProfileSectionCardGrid;
